import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router'

import { Lasku, Laskuasetukset, LaskuBase, UUDEN_LASKUN_AVAIN } from '@lemon/core-firebase/model/lasku'
import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'

import { LaskuService } from '../service/lasku/lasku.service'

import { from, Observable, of as observableOf } from 'rxjs'
import { filter, map, switchMap, take } from 'rxjs/operators'
import { AsiakasService, AsiakkaanAvainTiedot } from '../service/asiakas/asiakas.service'

export interface LaskuKatseleComponentData {
  juurilasku: Lasku
  kasiteltava: LaskuBase
  asetukset: Laskuasetukset
  tulosta: boolean
}

export interface LaskuKatseleComponentExistingData {
  juurilasku: Lasku
  kasiteltava: LaskuBase
}

export interface LaskuComponentExistingData {
  juurilasku: Lasku
  asetukset: Laskuasetukset
}

export interface LaskuComponentData {
  juurilasku: Lasku
  kasiteltava: LaskuBase
  asetukset: Laskuasetukset
}

@Injectable()
export class LaskuComponentDataResolve implements Resolve<LaskuComponentData> {
  private existingData: LaskuComponentExistingData = null

  constructor(
    private router: Router,
    private laskuService: LaskuService,
    private laskuKopioija: LaskuKopioija,
    private laskuSharedService: LaskuSharedService,
  ) {}

  asetaOlemassaolevaData(data: LaskuComponentExistingData) {
    this.existingData = data
  }

  resolve(route: ActivatedRouteSnapshot): Observable<LaskuComponentData> {
    const id = route.params['id']
    const kasiteltavaAvain = route.params['kasiteltavaAvain']
    const asiakasAvain = route.params['asiakasAvain']

    if (id === UUDEN_LASKUN_AVAIN) {
      return from(this.laskuKopioija.annaUusiLasku()).pipe(switchMap(lasku => this.haeAsetukset(lasku, lasku)))
    }

    if (this.existingData != null && this.existingData.juurilasku && this.existingData.juurilasku.avain === id) {
      return this.hoidaMuokattavat(asiakasAvain, this.existingData.juurilasku, kasiteltavaAvain)
    }

    return this.laskuService.getLaskuObservable(id).pipe(
      take(1),
      switchMap(juurilasku => {
        if (!juurilasku) {
          this.router.navigate(['asiakkaat', asiakasAvain, 'laskutus'])
          return observableOf(null)
        }

        return this.hoidaMuokattavat(asiakasAvain, juurilasku, kasiteltavaAvain)
      }),
    )
  }

  private hoidaMuokattavat(asiakasAvain: string, juurilasku: Lasku, kasiteltavaAvain: string): Observable<LaskuComponentData> {
    const edellinen = this.laskuSharedService.annaViimeisinKasiteltavaLasku(juurilasku)

    if (kasiteltavaAvain === UUDEN_LASKUN_AVAIN) {
      const muokattavaksiJuuri = this.laskuKopioija.copyLasku(juurilasku)
      const kasiteltava = this.laskuKopioija.kopioiLaskuMuokattavaksi(edellinen, null)
      this.laskuSharedService.lisaaLiittyviinLaskuihinIlmanMuutostenKeraamista(muokattavaksiJuuri, kasiteltava)
      return this.haeAsetukset(muokattavaksiJuuri, kasiteltava)
    }

    if (kasiteltavaAvain === 'hyvita') {
      const viimeinenTavallinen = this.laskuSharedService.annaViimeisinTavallinenLasku(juurilasku)
      const muokattavaksiJuuri = this.laskuKopioija.copyLasku(juurilasku)
      const muokattavaksiKasiteltava = this.laskuKopioija.kopioiLaskuHyvitettavaksi(viimeinenTavallinen, viimeinenTavallinen.asiakas)
      this.laskuSharedService.lisaaLiittyviinLaskuihinIlmanMuutostenKeraamista(muokattavaksiJuuri, muokattavaksiKasiteltava)
      return this.haeAsetukset(muokattavaksiJuuri, muokattavaksiKasiteltava)
    }

    if (edellinen.avain !== kasiteltavaAvain) {
      this.router.navigate(['asiakkaat', asiakasAvain, 'laskutus', 'laskut', juurilasku.avain, edellinen.avain])
      return observableOf(null)
    }

    const kopioidut = this.laskuKopioija.kopioiEiLahetettyMuokattavaksi(juurilasku, edellinen, null)
    return this.haeAsetukset(kopioidut.muokattavaJuuri, kopioidut.muokattavaKasiteltava)
  }

  private haeAsetukset(juurilasku: Lasku, kasiteltava: LaskuBase): Observable<LaskuComponentData> {
    return this.laskuService.asetuksetObservable.pipe(
      take(1),
      map(asetukset => {
        const data: LaskuComponentData = {
          asetukset: asetukset,
          juurilasku: juurilasku,
          kasiteltava: kasiteltava,
        }
        return data
      }),
    )
  }
}

@Injectable()
export class LaskuKatseleComponentDataResolve implements Resolve<LaskuKatseleComponentData> {
  private existingData: LaskuKatseleComponentExistingData = null

  constructor(
    private router: Router,
    private laskuService: LaskuService,
    private laskuKopioija: LaskuKopioija,
    private laskuSharedService: LaskuSharedService,
  ) {}

  asetaOlemassaolevaData(data: LaskuKatseleComponentExistingData) {
    this.existingData = data
  }

  resolve(route: ActivatedRouteSnapshot): Observable<LaskuKatseleComponentData> {
    const id = route.params['id']
    const kasiteltavaAvain = route.params['kasiteltavaAvain']
    const asiakasAvain = route.params['asiakasAvain']
    const toiminto = route.params['toiminto']

    if (id === UUDEN_LASKUN_AVAIN) {
      return from(this.laskuKopioija.annaUusiLasku()).pipe(switchMap(lasku => this.haeAsetukset({ juurilasku: lasku, kasiteltava: lasku }, toiminto)))
    }

    if (
      this.existingData != null &&
      this.existingData.juurilasku &&
      this.existingData.kasiteltava &&
      this.existingData.juurilasku.avain === id &&
      this.existingData.kasiteltava.avain === kasiteltavaAvain
    ) {
      return this.haeLaskunLaskuAsetukset(this.existingData, toiminto)
    }

    return this.laskuService.getLaskuObservable(id).pipe(
      take(1),
      switchMap(juurilasku => {
        if (!juurilasku) {
          this.router.navigate(['asiakkaat', asiakasAvain, 'laskutus'])
          return observableOf(null)
        }

        if (kasiteltavaAvain === UUDEN_LASKUN_AVAIN) {
          const edellinen = this.laskuSharedService.annaViimeisinKasiteltavaLasku(juurilasku)
          const muokattava = this.laskuKopioija.kopioiLaskuMuokattavaksi(edellinen, null)
          this.laskuSharedService.lisaaLiittyviinLaskuihinIlmanMuutostenKeraamista(juurilasku, muokattava)
          return this.haeLaskunLaskuAsetukset({ juurilasku: juurilasku, kasiteltava: muokattava }, toiminto)
        }

        const kasiteltava = this.laskuSharedService.annaKasiteltavaLasku(juurilasku, kasiteltavaAvain)
        return this.haeLaskunLaskuAsetukset({ juurilasku: juurilasku, kasiteltava: kasiteltava }, toiminto)
      }),
    )
  }

  private haeLaskunLaskuAsetukset(existingData: LaskuKatseleComponentExistingData, toiminto: string): Observable<LaskuKatseleComponentData> {
    // Toistuvan laskun aihioon tallennetut asetukset ovat vanha kopio, ja lähetys käyttää
    // aina tuoreita laskuasetuksia, joten myös esikatselun pitää näyttää tuoreet
    if (existingData.kasiteltava.asetukset && !existingData.juurilasku?.toistuvaLaskuaihio) {
      const data: LaskuKatseleComponentData = {
        asetukset: existingData.kasiteltava.asetukset,
        juurilasku: existingData.juurilasku,
        kasiteltava: existingData.kasiteltava,
        tulosta: toiminto === 'tulosta',
      }
      return observableOf(data)
    }
    return this.haeAsetukset(existingData, toiminto)
  }

  private haeAsetukset(existingData: LaskuKatseleComponentExistingData, toiminto: string): Observable<LaskuKatseleComponentData> {
    return this.laskuService.asetuksetObservable.pipe(
      take(1),
      map(asetukset => {
        const data: LaskuKatseleComponentData = {
          asetukset: asetukset,
          juurilasku: existingData.juurilasku,
          kasiteltava: existingData.kasiteltava,
          tulosta: toiminto === 'tulosta',
        }
        return data
      }),
    )
  }
}

@Injectable()
export class LaskuSelaaKatseleComponentDataResolve implements Resolve<LaskuKatseleComponentData> {
  private existingData: LaskuKatseleComponentExistingData = null

  constructor(
    private router: Router,
    private laskuService: LaskuService,
    private laskuKopioija: LaskuKopioija,
    private laskuSharedService: LaskuSharedService,
    private asiakasService: AsiakasService,
  ) {}

  asetaOlemassaolevaData(data: LaskuKatseleComponentExistingData) {
    this.existingData = data
  }

  resolve(route: ActivatedRouteSnapshot): Observable<LaskuKatseleComponentData> {
    const id = route.params['id']
    const kasiteltavaAvain = route.params['kasiteltavaAvain']
    const asiakasAvain = route.params['asiakasAvain']
    const toiminto = route.params['toiminto']

    if (id === UUDEN_LASKUN_AVAIN) {
      return from(this.laskuKopioija.annaUusiLasku()).pipe(switchMap(lasku => this.haeAsetuksetNykyiselleAsiakkaalle({ juurilasku: lasku, kasiteltava: lasku }, toiminto)))
    }

    if (
      this.existingData != null &&
      this.existingData.juurilasku &&
      this.existingData.kasiteltava &&
      this.existingData.juurilasku.avain === id &&
      this.existingData.kasiteltava.avain === kasiteltavaAvain
    ) {
      return this.haeLaskunLaskuAsetukset(this.existingData, toiminto)
    }

    return this.asiakasService.nykyinenAsiakasAvainObservable.pipe(
      filter<AsiakkaanAvainTiedot>(Boolean),
      take(1),
      switchMap(asiakas => {
        return this.laskuService.getLaskuObservableAsiakkaalle(asiakas.avain, id).pipe(
          take(1),
          switchMap(juurilasku => {
            if (!juurilasku) {
              this.router.navigate(['asiakkaat', asiakasAvain, 'laskut', 'selaa'])
              return observableOf(null)
            }

            if (kasiteltavaAvain === UUDEN_LASKUN_AVAIN) {
              const edellinen = this.laskuSharedService.annaViimeisinKasiteltavaLasku(juurilasku)
              const muokattava = this.laskuKopioija.kopioiLaskuMuokattavaksi(edellinen, null)
              this.laskuSharedService.lisaaLiittyviinLaskuihinIlmanMuutostenKeraamista(juurilasku, muokattava)
              return this.haeLaskunLaskuAsetukset({ juurilasku: juurilasku, kasiteltava: muokattava }, toiminto)
            }

            const kasiteltava = this.laskuSharedService.annaKasiteltavaLasku(juurilasku, kasiteltavaAvain)
            return this.haeLaskunLaskuAsetukset({ juurilasku: juurilasku, kasiteltava: kasiteltava }, toiminto)
          }),
        )
      }),
    )
  }

  private haeLaskunLaskuAsetukset(existingData: LaskuKatseleComponentExistingData, toiminto: string): Observable<LaskuKatseleComponentData> {
    if (existingData.kasiteltava.asetukset) {
      const data: LaskuKatseleComponentData = {
        asetukset: existingData.kasiteltava.asetukset,
        juurilasku: existingData.juurilasku,
        kasiteltava: existingData.kasiteltava,
        tulosta: toiminto === 'tulosta',
      }
      return observableOf(data)
    }
    return this.haeAsetuksetNykyiselleAsiakkaalle(existingData, toiminto)
  }

  private haeAsetuksetNykyiselleAsiakkaalle(existingData: LaskuKatseleComponentExistingData, toiminto: string): Observable<LaskuKatseleComponentData> {
    return this.asiakasService.nykyinenAsiakasAvainObservable.pipe(
      filter<AsiakkaanAvainTiedot>(Boolean),
      take(1),
      switchMap(asiakas => {
        return this.haeAsetukset(asiakas.asiakasId + '', asiakas.avain, existingData, toiminto)
      }),
    )
  }

  private haeAsetukset(asiakasId: string, asiakasAvain: string, existingData: LaskuKatseleComponentExistingData, toiminto: string): Observable<LaskuKatseleComponentData> {
    return this.laskuService.getLaskuAsetuksetAsiakkaalleObservable(asiakasId, asiakasAvain).pipe(
      filter<Laskuasetukset>(Boolean),
      take(1),
      map(asetukset => {
        const data: LaskuKatseleComponentData = {
          asetukset: asetukset,
          juurilasku: existingData.juurilasku,
          kasiteltava: existingData.kasiteltava,
          tulosta: toiminto === 'tulosta',
        }
        return data
      }),
    )
  }
}
